import { useEffect, useState } from 'react';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  AvailabilityStatusService,
  JobAvailabilityType,
  JobPreference,
  Sitter,
} from 'us-web-services';
import toast from 'react-hot-toast';

import { Button, ButtonType } from '../../../components/Button';

interface ModalBase {
  isOpen: boolean;
  onClose: () => void;
}

// NoteModal, flag-content, ConfirmModal, ProfileVideoModal promoted to us-react-components
// (SitterNoteModal, FlagProfileModal). Import them directly from the shared
// library in the SitterProfile container.

// -- Availability Modal (job seeking status) -------------------------------
// Angular source: apps/angular/src/js/ng/sitter/profile/availability-status-modal/
// Note: the recurring-availability snapshot grid (edit-recurring-snapshot)
// is not ported here — the sitter edits the full weekly calendar from
// /app/sitter/:id/calendar. Job preferences + description + interview times
// are saved via AvailabilityStatusService.
const STATUS_DESCRIPTION_MAX = 500;

export const AvailabilityModal = ({
  isOpen,
  onClose,
  sitter,
  canEdit,
  onSaved,
  lastCalendarUpdateLabel,
}: ModalBase & {
  sitter: Sitter;
  canEdit: boolean;
  onSaved?: () => void | Promise<void>;
  lastCalendarUpdateLabel?: string;
}): JSX.Element => {
  const initialPrefs = sitter.availabilityStatus?.jobPreferences ?? [];
  const hasType = (t: JobAvailabilityType): boolean =>
    initialPrefs.some((p) => p.type === t);

  const [occasional, setOccasional] = useState(hasType(JobAvailabilityType.occasional));
  const [partTime, setPartTime] = useState(hasType(JobAvailabilityType.partTime));
  const [fullTime, setFullTime] = useState(hasType(JobAvailabilityType.fullTime));
  const [description, setDescription] = useState(
    sitter.availabilityStatus?.description ?? '',
  );
  const [interviewTimes, setInterviewTimes] = useState(
    (sitter.availabilityStatus as { interviewTimes?: string } | undefined)
      ?.interviewTimes ?? '',
  );
  const [saving, setSaving] = useState(false);
  const { t } = useSharedTranslation(namespaces.caregiver);

  useEffect(() => {
    if (isOpen) {
      setOccasional(hasType(JobAvailabilityType.occasional));
      setPartTime(hasType(JobAvailabilityType.partTime));
      setFullTime(hasType(JobAvailabilityType.fullTime));
      setDescription(sitter.availabilityStatus?.description ?? '');
      setInterviewTimes(
        (sitter.availabilityStatus as { interviewTimes?: string } | undefined)
          ?.interviewTimes ?? '',
      );
    }

  }, [isOpen, sitter.id]);

  const save = async (): Promise<void> => {
    if (!sitter.id) return;
    setSaving(true);
    const prefs: JobPreference[] = [];
    if (occasional) prefs.push({ type: JobAvailabilityType.occasional });
    if (partTime) prefs.push({ type: JobAvailabilityType.partTime });
    if (fullTime) prefs.push({ type: JobAvailabilityType.fullTime });
    const payload = {
      sitterId: sitter.id,
      jobPreferences: prefs,
      description,
      interviewTimes,
    } as unknown as Parameters<typeof AvailabilityStatusService.create>[0];
    try {
      const statusId = (sitter.availabilityStatus as { id?: number } | undefined)?.id;
      if (statusId) {
        await AvailabilityStatusService.patch(
          statusId,
          { id: statusId, ...payload } as unknown as Parameters<
            typeof AvailabilityStatusService.patch
          >[1],
        );
      } else {
        await AvailabilityStatusService.create(payload);
      }
      toast.success(t('pages.sitterProfile.availability.updated'));
      await onSaved?.();
      onClose();
    } catch {
      toast.error("Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('pages.sitterProfile.modals.availability')}
        className='w-full max-w-2xl'
      >
        {sitter.availabilityStatus?.description ? (
          <p className='mb-3'>{sitter.availabilityStatus.description}</p>
        ) : (
          <p className='mb-3 text-neutral-500'>No availability details posted.</p>
        )}
        <p className='text-sm text-neutral-600'>
          For the full weekly schedule,{' '}
          <a href={`/app/sitter/${sitter.id}/calendar`} className='underline'>
            view the calendar
          </a>
          .
        </p>
      </Modal>
    );
  }

  const descRemaining = STATUS_DESCRIPTION_MAX - description.length;
  const interviewRemaining = STATUS_DESCRIPTION_MAX - interviewTimes.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.availability.heading')}
      className='w-full max-w-3xl'
    >
      <h3 className='mt-2 mb-2'>What type of jobs are you looking for?</h3>

      <div className='us-card rounded p-3 mb-3'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <label className='flex items-center gap-2'>
            <input
              id='occasional-jobs'
              type='checkbox'
              checked={occasional}
              onChange={(e): void => setOccasional(e.target.checked)}
            />
            <b>Occasional jobs</b>
          </label>
          <div>
            <h5 className='mb-1 font-semibold'>Availability for occasional jobs:</h5>
            <div>
              <i className='fa-regular fa-calendar text-sapphire mr-1' />
              Keep your main calendar up to date
            </div>
            {lastCalendarUpdateLabel && (
              <div className='italic text-sm text-neutral-600'>
                Last updated {lastCalendarUpdateLabel}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='us-card rounded p-3 mb-3'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <label className='flex items-center gap-2 mb-3'>
              <input
                id='part-time-jobs'
                type='checkbox'
                checked={partTime}
                onChange={(e): void => setPartTime(e.target.checked)}
              />
              <b>Part-time jobs</b>
            </label>
            <label className='flex items-center gap-2 mb-3'>
              <input
                id='full-time-jobs'
                type='checkbox'
                checked={fullTime}
                onChange={(e): void => setFullTime(e.target.checked)}
              />
              <b>Full-time jobs</b>
            </label>
            <h5 className='font-semibold mb-0'>More details about your availability:</h5>
            <div className='text-right text-xs italic text-neutral-500 mb-1'>
              {descRemaining} characters remaining
            </div>
            <textarea
              aria-label={t('pages.sitterProfile.modals.availabilityDescAriaLabel')}
              className='form-control w-full border border-neutral-300 rounded p-2'
              rows={3}
              maxLength={STATUS_DESCRIPTION_MAX}
              value={description}
              onChange={(e): void => setDescription(e.target.value)}
              placeholder={t('pages.sitterProfile.modals.availabilityDescPlaceholder')}
            />
          </div>
          <div>
            <h5 className='font-semibold mb-0'>{t('pages.sitterProfile.availability.snapshotHeading')}</h5>
            <p className='text-sm mb-2'>
              Select the times you are generally available for recurring jobs on your{' '}
              <a href={`/app/sitter/${sitter.id}/calendar`} className='underline'>
                calendar
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div>
          <h5 className='font-semibold mb-0'>When are you available to interview?</h5>
          <div className='text-sm mb-1'>
            Suggest some good days or times. This will only display to families sending
            interview requests.
          </div>
          <div className='text-right text-xs italic text-neutral-500 mb-1'>
            {interviewRemaining} characters remaining
          </div>
          <textarea
            aria-label={t('pages.sitterProfile.modals.interviewTimesAriaLabel')}
            className='form-control w-full border border-neutral-300 rounded p-2'
            rows={3}
            maxLength={STATUS_DESCRIPTION_MAX}
            value={interviewTimes}
            onChange={(e): void => setInterviewTimes(e.target.value)}
            placeholder={t('pages.sitterProfile.modals.interviewTimesPlaceholder')}
          />
        </div>
        <div className='text-center self-center'>
          <div className='mb-3'>
            <b>{t('pages.sitterProfile.availability.takingBreak')}</b> Simply uncheck the boxes above.
          </div>
          <Button
            id='availability-status-modal-save'
            buttonType={ButtonType.primary}
            className='btn-block w-full'
            disabled={saving}
            onClick={save}
          >
            {saving ? (
              <>
                <i className='fa-solid fa-spinner fa-spin mr-2' /> Loading
              </>
            ) : (
              'Update'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// -- Simple Edit Modals (self-view) ----------------------------------------
// Covers the common pattern: one textarea or select + Save button that
// PATCHes the sitter and updates parent state. Each Angular modal it stands
// in for is listed in the heading prop used when opened.

export const TextAreaEditModal = ({
  isOpen,
  onClose,
  title,
  label,
  helperText,
  initialValue,
  maxLength,
  onSave,
}: ModalBase & {
  title: string;
  label: string;
  helperText?: string;
  initialValue: string;
  maxLength?: number;
  onSave: (value: string) => Promise<void>;
}): JSX.Element => {
  const [value, setValue] = useState(initialValue);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setValue(initialValue);
      setError('');
    }
  }, [isOpen, initialValue]);

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onSave(value);
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <label className='block mb-1 font-semibold'>{label}</label>
      <textarea
        className='w-full border border-neutral-300 rounded p-2'
        rows={6}
        maxLength={maxLength}
        value={value}
        onChange={(e): void => setValue(e.target.value)}
      />
      {helperText && <p className='text-sm text-neutral-600 mt-1'>{helperText}</p>}
      {maxLength && (
        <div className='text-sm text-neutral-600 mt-1'>
          {maxLength - value.length} characters remaining
        </div>
      )}
      {error && <p className='text-red-600 text-sm mt-2'>{error}</p>}
    </Modal>
  );
};

export const MultiSelectEditModal = ({
  isOpen,
  onClose,
  title,
  options,
  initialSelected,
  onSave,
  allowOther = false,
  otherLabel = 'Other:',
  otherPlaceholder = 'Other',
  initialOther = '',
}: ModalBase & {
  title: string;
  options: Array<{ id: number; name: string; description?: string }>;
  initialSelected: number[];
  onSave: (ids: number[], otherName?: string) => Promise<void>;
  allowOther?: boolean;
  otherLabel?: string;
  otherPlaceholder?: string;
  initialOther?: string;
}): JSX.Element => {
  const [selected, setSelected] = useState<Set<number>>(new Set(initialSelected));
  const [other, setOther] = useState<string>(initialOther);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(initialSelected));
      setOther(initialOther);
      setError('');
    }
  }, [isOpen, initialSelected, initialOther]);

  const toggle = (id: number): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onSave([...selected], allowOther ? other.trim() : undefined);
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className='space-y-2 max-h-[60vh] overflow-auto'>
        {options.map((option) => (
          <label key={option.id} className='flex items-start gap-2'>
            <input
              type='checkbox'
              checked={selected.has(option.id)}
              onChange={(): void => toggle(option.id)}
              className='mt-1'
            />
            <span>
              <span className='font-medium'>{option.name}</span>
              {option.description && (
                <span className='text-sm block text-neutral-600'>{option.description}</span>
              )}
            </span>
          </label>
        ))}
      </div>
      {allowOther && (
        <div className='mt-4 flex items-center gap-3'>
          <label htmlFor='other-edit-input' className='font-semibold whitespace-nowrap'>
            {otherLabel}
          </label>
          <input
            id='other-edit-input'
            type='text'
            className='form-control flex-1 border border-neutral-300 rounded px-3 py-2'
            placeholder={otherPlaceholder}
            value={other}
            onChange={(e): void => setOther(e.target.value)}
          />
        </div>
      )}
      {error && <p className='text-red-600 text-sm mt-2'>{error}</p>}
    </Modal>
  );
};

// -- Rates Edit Modal ------------------------------------------------------
// Angular source: apps/angular/src/js/ng/sitter/profile/edit-rates-modal/
const pluralizeKidsLabel = (n: number): string =>
  n === 1 ? '1 child, senior, or pet' : `${n} children, seniors, or pets`;

const MINIMUM_HOUR_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'None' },
  { value: 2, label: '2 hours' },
  { value: 3, label: '3 hours' },
  { value: 4, label: '4 hours' },
  { value: 5, label: '5 hours' },
  { value: 6, label: '6 hours' },
  { value: 8, label: '8 hours' },
];

export const RatesEditModal = ({
  isOpen,
  onClose,
  initialRates,
  initialMaxKids,
  initialMinimumHours,
  initialAcceptsFixedRates,
  initialWillSitSingleFamily,
  suggestedRate,
  onSave,
}: ModalBase & {
  initialRates: Array<{ minimumRate?: number; maximumRate?: number }>;
  initialMaxKids?: number;
  initialMinimumHours?: number;
  initialAcceptsFixedRates?: boolean;
  initialWillSitSingleFamily?: boolean;
  suggestedRate?: { rate?: number; defaultRate?: boolean };
  onSave: (patch: {
    rates: Array<{ minimumRate: number; maximumRate: number }>;
    maxKids?: number;
    minimumHours?: number;
    acceptsFixedRates?: boolean;
    willSitSingleFamily?: boolean;
  }) => Promise<void>;
}): JSX.Element => {
  const [rates, setRates] = useState(() =>
    [0, 1, 2, 3].map((i) => ({
      minimumRate: String(initialRates[i]?.minimumRate ?? ''),
      maximumRate: String(initialRates[i]?.maximumRate ?? ''),
    })),
  );
  const [maxKids, setMaxKids] = useState(String(initialMaxKids ?? ''));
  const [minimumHours, setMinimumHours] = useState(String(initialMinimumHours ?? ''));
  const [acceptsFixedRates, setAcceptsFixedRates] = useState(!!initialAcceptsFixedRates);
  const [willSitSingleFamily, setWillSitSingleFamily] = useState(!!initialWillSitSingleFamily);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { t } = useSharedTranslation(namespaces.caregiver);

  useEffect(() => {
    if (isOpen) {
      setRates(
        [0, 1, 2, 3].map((i) => ({
          minimumRate: String(initialRates[i]?.minimumRate ?? ''),
          maximumRate: String(initialRates[i]?.maximumRate ?? ''),
        })),
      );
      setMaxKids(String(initialMaxKids ?? ''));
      setMinimumHours(String(initialMinimumHours ?? ''));
      setAcceptsFixedRates(!!initialAcceptsFixedRates);
      setWillSitSingleFamily(!!initialWillSitSingleFamily);
      setError('');
    }
  }, [isOpen, initialRates, initialMaxKids, initialMinimumHours, initialAcceptsFixedRates, initialWillSitSingleFamily]);

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      const cleaned = rates
        .map((r) => ({
          minimumRate: Number(r.minimumRate) || 0,
          maximumRate: Number(r.maximumRate) || Number(r.minimumRate) || 0,
        }))
        .filter((r) => r.minimumRate > 0);
      if (cleaned[0] && cleaned[0].minimumRate < 8) {
        setError(t('pages.sitterProfile.rates.minimumError'));
        setSubmitting(false);
        return;
      }
      await onSave({
        rates: cleaned,
        maxKids: maxKids ? Number(maxKids) : undefined,
        minimumHours: minimumHours ? Number(minimumHours) : undefined,
        acceptsFixedRates,
        willSitSingleFamily,
      });
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editRates')}
      className='w-full max-w-3xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            id='save-sitter-rates'
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
        <div>
          <h4 className='mb-1 font-semibold'>
            {t('pages.sitterProfile.rates.ratesByCountHeading')}
          </h4>
          {suggestedRate?.rate != null && suggestedRate.rate > 0 && (
            <div className='mb-2'>
              {suggestedRate.defaultRate
                ? `On average, caregivers charge $${suggestedRate.rate}/hr`
                : `Caregivers in your area are charging $${suggestedRate.rate}/hr`}
            </div>
          )}
          <div className='mb-3 text-sm'>
            Based on our data, caregivers often start out with lower rates until they can
            build up their reviews and repeat families.
          </div>
          <div className='space-y-2'>
            {rates.map((r, i) => (
              <div key={i} className='grid grid-cols-12 gap-2 items-center'>
                <span className='col-span-6 text-sm'>{pluralizeKidsLabel(i + 1)}</span>
                <div className='col-span-3 flex items-center border border-neutral-300 rounded'>
                  <span className='px-2 bg-neutral-100'>$</span>
                  <input
                    id={`min-rate-${i + 1}`}
                    aria-label={`Minimum rate for ${i + 1}`}
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    maxLength={2}
                    className='w-full px-2 py-1 text-sm'
                    value={r.minimumRate}
                    onChange={(e): void =>
                      setRates((prev) =>
                        prev.map((row, idx) =>
                          idx === i ? { ...row, minimumRate: e.target.value } : row,
                        ),
                      )
                    }
                  />
                </div>
                <span className='col-span-1 text-center'>-</span>
                <div className='col-span-2 flex items-center border border-neutral-300 rounded'>
                  <span className='px-2 bg-neutral-100'>$</span>
                  <input
                    id={`max-rate-${i + 1}`}
                    aria-label={`Maximum rate for ${i + 1}`}
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    maxLength={2}
                    className='w-full px-2 py-1 text-sm'
                    value={r.maximumRate}
                    onChange={(e): void =>
                      setRates((prev) =>
                        prev.map((row, idx) =>
                          idx === i ? { ...row, maximumRate: e.target.value } : row,
                        ),
                      )
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className='mb-1 font-semibold'>{t('pages.sitterProfile.rates.maxHeading')}</h4>
          <select
            id='max-num-children'
            aria-label={t('pages.sitterProfile.modals.maxChildrenAriaLabel')}
            className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-4'
            value={maxKids}
            onChange={(e): void => setMaxKids(e.target.value)}
          >
            <option value=''>Select</option>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <option key={n} value={n}>
                {pluralizeKidsLabel(n)}
              </option>
            ))}
          </select>

          <h4 className='mb-1 font-semibold'>{t('pages.sitterProfile.rates.minHoursHeading')}</h4>
          <select
            id='minimum-hours'
            aria-label={t('pages.sitterProfile.modals.minHoursAriaLabel')}
            className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-3'
            value={minimumHours}
            onChange={(e): void => setMinimumHours(e.target.value)}
          >
            {MINIMUM_HOUR_OPTIONS.map((h) => (
              <option key={h.value} value={h.value}>
                {h.label}
              </option>
            ))}
          </select>

          <h4 className='mb-2 font-semibold'>
            {t('pages.sitterProfile.rates.considerationsHeading')}
          </h4>
          <label className='flex items-start gap-2 mb-2 text-sm'>
            <input
              id='flat-rates-checkbox'
              type='checkbox'
              checked={acceptsFixedRates}
              onChange={(e): void => setAcceptsFixedRates(e.target.checked)}
            />
            {t('pages.sitterProfile.rates.openToFlatRates')}
          </label>
          <label className='flex items-start gap-2 text-sm'>
            <input
              id='single-family-checkbox'
              type='checkbox'
              checked={willSitSingleFamily}
              onChange={(e): void => setWillSitSingleFamily(e.target.checked)}
            />
            {t('pages.sitterProfile.rates.willingExclusive')}
          </label>
        </div>
      </div>

      {error && <p className='text-red-600 text-sm mt-3'>{error}</p>}
    </Modal>
  );
};

// -- Location Edit Modal ---------------------------------------------------
export const LocationEditModal = ({
  isOpen,
  onClose,
  initialAddress,
  initialWorksWithin,
  onSave,
}: ModalBase & {
  initialAddress?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    neighborhood?: string;
  };
  initialWorksWithin?: number;
  onSave: (patch: {
    address: {
      street?: string;
      street2?: string;
      city: string;
      state: string;
      zip: string;
      neighborhood?: string;
    };
    worksWithin?: number;
  }) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [street, setStreet] = useState(initialAddress?.street ?? '');
  const [street2, setStreet2] = useState(initialAddress?.street2 ?? '');
  const [city, setCity] = useState(initialAddress?.city ?? '');
  const [state, setState] = useState(initialAddress?.state ?? '');
  const [zip, setZip] = useState(initialAddress?.zip ?? '');
  const [neighborhood, setNeighborhood] = useState(initialAddress?.neighborhood ?? '');
  const [worksWithin, setWorksWithin] = useState(String(initialWorksWithin ?? ''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStreet(initialAddress?.street ?? '');
      setStreet2(initialAddress?.street2 ?? '');
      setCity(initialAddress?.city ?? '');
      setState(initialAddress?.state ?? '');
      setZip(initialAddress?.zip ?? '');
      setNeighborhood(initialAddress?.neighborhood ?? '');
      setWorksWithin(String(initialWorksWithin ?? ''));
      setError('');
    }
  }, [isOpen, initialAddress, initialWorksWithin]);

  const submit = async (): Promise<void> => {
    if (!city.trim() || !state.trim() || !zip.trim()) {
      setError('City, state, and ZIP are required.');
      return;
    }
    setSubmitting(true);
    try {
      await onSave({
        address: {
          street: street || undefined,
          street2: street2 || undefined,
          city: city.trim(),
          state: state.trim().toUpperCase(),
          zip: zip.trim(),
          neighborhood: neighborhood || undefined,
        },
        worksWithin: worksWithin ? Number(worksWithin) : undefined,
      });
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editLocation')}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <p className='mb-1'>Add your street address:</p>
      <p className='text-sm mb-3 text-neutral-600'>
        To calculate distance. Your address will NOT be public.
      </p>
      <div className='grid grid-cols-12 gap-2 mb-2'>
        <input
          type='text'
          className='col-span-7 form-control border border-neutral-300 rounded px-3 py-2'
          placeholder={t('pages.sitterProfile.modals.placeholderStreet')}
          value={street}
          onChange={(e): void => setStreet(e.target.value)}
        />
        <input
          type='text'
          className='col-span-5 form-control border border-neutral-300 rounded px-3 py-2'
          placeholder={t('pages.sitterProfile.modals.placeholderApt')}
          value={street2}
          onChange={(e): void => setStreet2(e.target.value)}
        />
      </div>
      <div className='grid grid-cols-12 gap-2 mb-2'>
        <input
          type='text'
          className='col-span-7 form-control border border-neutral-300 rounded px-3 py-2'
          placeholder={t('pages.sitterProfile.modals.placeholderCity')}
          value={city}
          onChange={(e): void => setCity(e.target.value)}
        />
        <input
          type='text'
          className='col-span-5 form-control border border-neutral-300 rounded px-3 py-2'
          placeholder={t('pages.sitterProfile.modals.placeholderState')}
          maxLength={2}
          value={state}
          onChange={(e): void => setState(e.target.value)}
        />
      </div>
      <div className='grid grid-cols-12 gap-2 mb-2'>
        <input
          type='text'
          className='col-span-4 form-control border border-neutral-300 rounded px-3 py-2'
          placeholder={t('pages.sitterProfile.modals.placeholderZip')}
          aria-label={t('pages.sitterProfile.modals.zipAriaLabel')}
          maxLength={5}
          value={zip}
          onChange={(e): void => setZip(e.target.value)}
        />
        <input
          type='text'
          className='col-span-8 form-control border border-neutral-300 rounded px-3 py-2'
          placeholder={t('pages.sitterProfile.modals.placeholderNeighborhood')}
          value={neighborhood}
          onChange={(e): void => setNeighborhood(e.target.value)}
        />
      </div>
      <label htmlFor='works-within-distance' className='block font-semibold mb-1 mt-5 text-sm'>
        Willing to work within:
      </label>
      <select
        id='works-within-distance'
        className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-3'
        value={worksWithin}
        onChange={(e): void => setWorksWithin(e.target.value)}
      >
        <option value=''>Any distance</option>
        <option value='3'>3 miles</option>
        <option value='5'>5 miles</option>
        <option value='10'>10 miles</option>
        <option value='15'>15 miles</option>
        <option value='25'>25 miles</option>
        <option value='50'>50 miles</option>
      </select>
      {error && <p className='text-red-600 text-sm mb-2'>{error}</p>}
    </Modal>
  );
};

// -- Age Experience Edit Modal ---------------------------------------------
// Angular source: apps/angular/src/js/ng/sitter/profile/edit-age-experience-modal/
type AgeKey = 'infant' | 'toddler' | 'school';

const AGE_LABEL: Record<AgeKey, { title: string; detail: string; suffix: string }> = {
  infant: {
    title: 'Infants',
    detail: '(under 1 year old)',
    suffix: 'infants',
  },
  toddler: {
    title: 'Toddlers',
    detail: '(1-3 years old)',
    suffix: 'toddlers',
  },
  school: {
    title: 'School age',
    detail: '(4+ years old)',
    suffix: 'school age children',
  },
};

const formatExperienceLabel = (suffix: string, label: string): string => {
  if (label.indexOf('Not willing') > -1) {
    return `${label} ${suffix}`;
  }
  return label;
};

export const AgeExperienceEditModal = ({
  isOpen,
  onClose,
  ageExperiences,
  initialExperiences,
  onSave,
}: ModalBase & {
  ageExperiences: Array<{ id: number; label: string }>;
  initialExperiences?: {
    infant?: { id?: number; label?: string };
    toddler?: { id?: number; label?: string };
    school?: { id?: number; label?: string };
  };
  onSave: (experiences: {
    infant: { id: number };
    toddler: { id: number };
    school: { id: number };
  }) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [infantId, setInfantId] = useState<number | ''>(
    initialExperiences?.infant?.id ?? '',
  );
  const [toddlerId, setToddlerId] = useState<number | ''>(
    initialExperiences?.toddler?.id ?? '',
  );
  const [schoolId, setSchoolId] = useState<number | ''>(
    initialExperiences?.school?.id ?? '',
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setInfantId(initialExperiences?.infant?.id ?? '');
      setToddlerId(initialExperiences?.toddler?.id ?? '');
      setSchoolId(initialExperiences?.school?.id ?? '');
    }
  }, [isOpen, initialExperiences]);

  const submit = async (): Promise<void> => {
    if (!infantId || !toddlerId || !schoolId) return;
    setSubmitting(true);
    try {
      await onSave({
        infant: { id: Number(infantId) },
        toddler: { id: Number(toddlerId) },
        school: { id: Number(schoolId) },
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const renderGroup = (
    key: AgeKey,
    value: number | '',
    setter: (v: number | '') => void,
  ): JSX.Element => (
    <div className='mb-4'>
      <div className='font-semibold'>
        {AGE_LABEL[key].title} <span className='font-normal'>{AGE_LABEL[key].detail}</span>
      </div>
      <select
        id={`edit-sitter-exp-${key === 'school' ? 'school-age' : key}`}
        aria-label={`${AGE_LABEL[key].title} experience`}
        className='form-control w-full border border-neutral-300 rounded px-3 py-2 mt-1'
        value={value}
        onChange={(e): void => setter(e.target.value ? Number(e.target.value) : '')}
      >
        <option value=''>Select</option>
        {ageExperiences.map((exp) => (
          <option key={exp.id} value={exp.id}>
            {formatExperienceLabel(AGE_LABEL[key].suffix, exp.label)}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editAgeExperience')}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            id='save-age-experience'
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <h4 className='mb-3'>
        What are the ages you are willing to babysit? How many years of experience do you have?
      </h4>
      {renderGroup('infant', infantId, setInfantId)}
      {renderGroup('toddler', toddlerId, setToddlerId)}
      {renderGroup('school', schoolId, setSchoolId)}
    </Modal>
  );
};

// -- Education Edit Modal --------------------------------------------------
// Angular source: apps/angular/src/js/ng/sitter/profile/edit-education-modal/
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MIN_GRADUATION_YEAR = 1900;
const maxGraduationYear = (): number => new Date().getFullYear() + 10;

const validateGraduationYear = (year: string): boolean => {
  if (!year) return true;
  if (!/^\d{4}$/.test(year)) return false;
  const n = Number(year);
  return n >= MIN_GRADUATION_YEAR && n <= maxGraduationYear();
};

export const EducationEditModal = ({
  isOpen,
  onClose,
  collegeOptions,
  studyAreaOptions,
  initialEducation,
  onSave,
  onDelete,
}: ModalBase & {
  collegeOptions: Array<{ id: number; name: string }>;
  studyAreaOptions: Array<{ id: number; name: string }>;
  initialEducation?: {
    id?: number;
    college?: { id?: number; name?: string };
    areaOfStudy?: { id?: number; name?: string };
    graduation?: string;
  };
  onSave: (data: {
    id?: number;
    collegeId: number;
    areaOfStudyId?: number;
    graduationMonth?: string;
    graduationYear?: string;
  }) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}): JSX.Element => {
  const initialYear = initialEducation?.graduation
    ? new Date(initialEducation.graduation).getFullYear().toString()
    : '';
  const initialMonth = initialEducation?.graduation
    ? MONTHS_SHORT[new Date(initialEducation.graduation).getMonth()]
    : '';

  const [collegeId, setCollegeId] = useState<number | ''>(
    initialEducation?.college?.id ?? '',
  );
  const [studyAreaId, setStudyAreaId] = useState<number | ''>(
    initialEducation?.areaOfStudy?.id ?? '',
  );
  const [month, setMonth] = useState(initialMonth);
  const [year, setYear] = useState(initialYear);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useSharedTranslation(namespaces.caregiver);

  useEffect(() => {
    if (isOpen) {
      setCollegeId(initialEducation?.college?.id ?? '');
      setStudyAreaId(initialEducation?.areaOfStudy?.id ?? '');
      setMonth(initialMonth);
      setYear(initialYear);
    }
  }, [isOpen, initialEducation, initialMonth, initialYear]);

  const yearValid = validateGraduationYear(year);

  const submit = async (): Promise<void> => {
    if (!collegeId) return;
    if (!yearValid) return;
    setSubmitting(true);
    try {
      await onSave({
        id: initialEducation?.id,
        collegeId: Number(collegeId),
        areaOfStudyId: studyAreaId ? Number(studyAreaId) : undefined,
        graduationMonth: month || undefined,
        graduationYear: year || undefined,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const clear = async (): Promise<void> => {
    if (!initialEducation?.id) return;
    setSubmitting(true);
    try {
      await onDelete(initialEducation.id);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editEducation')}
      className='w-full max-w-2xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          {initialEducation?.id && (
            <Button
              id='delete-sitter-education'
              buttonType={ButtonType.secondary}
              disabled={submitting}
              onClick={clear}
            >
              Clear
            </Button>
          )}
          <Button
            id='save-sitter-education'
            buttonType={ButtonType.primary}
            disabled={!collegeId || !yearValid || submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className='grid grid-cols-12 gap-3 mb-3 items-center'>
        <label className='col-span-4 font-semibold'>College (most recent):</label>
        <select
          className='col-span-8 form-control border border-neutral-300 rounded px-3 py-2'
          value={collegeId}
          onChange={(e): void =>
            setCollegeId(e.target.value ? Number(e.target.value) : '')
          }
        >
          <option value=''>Select college</option>
          {collegeOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className='grid grid-cols-12 gap-3 mb-3 items-center'>
        <label className='col-span-4 font-semibold'>Area of study:</label>
        <select
          className='col-span-8 form-control border border-neutral-300 rounded px-3 py-2'
          value={studyAreaId}
          onChange={(e): void =>
            setStudyAreaId(e.target.value ? Number(e.target.value) : '')
          }
        >
          <option value=''>Select area of study</option>
          {studyAreaOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className='grid grid-cols-12 gap-3 mb-1 items-center'>
        <label htmlFor='select-graduation-month' className='col-span-4'>
          Graduation date:
        </label>
        <select
          id='select-graduation-month'
          aria-label={t('pages.sitterProfile.modals.expectedGradMonthAriaLabel')}
          className='col-span-4 form-control border border-neutral-300 rounded px-3 py-2'
          value={month}
          onChange={(e): void => setMonth(e.target.value)}
        >
          <option value=''>Month</option>
          {MONTHS_SHORT.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <input
          id='input-graduation-year'
          aria-label={t('pages.sitterProfile.modals.expectedGradYearAriaLabel')}
          type='tel'
          placeholder={t('pages.sitterProfile.modals.placeholderYear')}
          className={`col-span-4 form-control border rounded px-3 py-2 ${
            yearValid ? 'border-neutral-300' : 'border-red-500'
          }`}
          value={year}
          onChange={(e): void => setYear(e.target.value)}
        />
      </div>
      {yearValid ? (
        <p className='text-sm text-right mb-3'>
          If you&apos;re still in school, enter your expected graduation date.
        </p>
      ) : (
        <p className='text-red-600 text-sm text-right mb-3'>
          Enter a valid year between {MIN_GRADUATION_YEAR} and {maxGraduationYear()}
        </p>
      )}
    </Modal>
  );
};

// -- Certifications Edit Modal ---------------------------------------------
// Angular source: apps/angular/src/js/ng/sitter/profile/edit-certifications-modal/
export const CertificationsEditModal = ({
  isOpen,
  onClose,
  certifications,
  initialSelected,
  onSave,
}: ModalBase & {
  certifications: Array<{ id: number; name: string }>;
  initialSelected: Array<{ id: number; name: string }>;
  onSave: (selected: Array<{ id: number; name: string }>) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [selected, setSelected] = useState<Record<string, { id: number; name: string }>>(
    () => Object.fromEntries(initialSelected.map((c) => [c.name, c])),
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(Object.fromEntries(initialSelected.map((c) => [c.name, c])));
    }
  }, [isOpen, initialSelected]);

  const toggle = (cert: { id: number; name: string }): void => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[cert.name]) delete next[cert.name];
      else next[cert.name] = cert;
      return next;
    });
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onSave(Object.values(selected));
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const col1 = certifications.slice(0, 5);
  const col2 = certifications.slice(5, 10);

  const renderCol = (list: Array<{ id: number; name: string }>): JSX.Element => (
    <div className='space-y-2'>
      {list.map((cert) => (
        <label key={cert.id} className='flex items-start gap-2 cursor-pointer'>
          <input
            id={`certification-${cert.name}`}
            type='checkbox'
            checked={!!selected[cert.name]}
            onChange={(): void => toggle(cert)}
            className='mt-1'
          />
          <span>{cert.name}</span>
        </label>
      ))}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editCertifications')}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            id='save-certifications'
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3'>
        {renderCol(col1)}
        {renderCol(col2)}
      </div>
    </Modal>
  );
};
